import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Check, ChevronDown } from 'lucide-react'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'

export interface ChipOption {
  value: string
  label: string
}

interface FilterChipProps {
  label: string
  valueText: string
  options: ChipOption[]
  selected: string[]
  onChange: (values: string[]) => void
}

export const chipBase =
  'inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-meta transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
/** A chip with a filter set: the brand tint, so what narrows the list is visible at a glance. */
export const chipOn = 'border border-primary/40 bg-accent font-medium text-accent-foreground'
export const chipOff = 'border border-line-strong bg-background text-ink-body hover:border-ink-placeholder hover:text-ink'

/** Outlined pill opening a multi-select popover. */
export function FilterChip({ label, valueText, options, selected, onChange }: FilterChipProps) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const active = selected.length > 0
  const toggle = (value: string) =>
    onChange(selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value])

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-haspopup="listbox"
          className={cn(chipBase, active ? chipOn : chipOff)}
        >
          {label}: {valueText}
          <ChevronDown className="size-3.5 opacity-60" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-64 p-1">
        <ul role="listbox" aria-multiselectable="true" aria-label={label} className="max-h-72 overflow-y-auto">
          {options.map((o) => {
            const checked = selected.includes(o.value)
            return (
              <li key={o.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={checked}
                  onClick={() => toggle(o.value)}
                  className="flex w-full items-center gap-2 rounded-sm px-2 py-1.5 text-left text-meta text-ink hover:bg-surface-subtle"
                >
                  <span
                    className={cn(
                      'flex size-4 shrink-0 items-center justify-center rounded-sm border',
                      checked ? 'border-primary bg-primary text-primary-foreground' : 'border-line-strong',
                    )}
                  >
                    {checked ? <Check className="size-3" /> : null}
                  </span>
                  {o.label}
                </button>
              </li>
            )
          })}
        </ul>
        {active ? (
          <div className="border-t border-line p-1">
            <button
              type="button"
              onClick={() => onChange([])}
              className="w-full rounded-sm px-2 py-1.5 text-left text-meta text-ink-muted hover:bg-surface-subtle"
            >
              {t('queue.chips.clear')}
            </button>
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  )
}
